// @flow
import moment from 'moment';
import {
  DEFAULT_EMAIL_ENTRY,
  DEFAULT_PHONE_ENTRY,
} from '../../../constants/contactTypes';
import { mapFloorPlanOptions } from '../../../utils/floorPlan';
import {
  filterFloorPlanOptions,
  getBedroomOptions,
  getPrefilledBedrooms,
} from './bedrooms';

// The three values AdditionalOccupant.type takes. A fourth would fall into
// neither bucket rather than silently inflating one.
const ADULT_OCCUPANT_TYPES = ['Spouse', 'Other Adult'];
const MINOR_OCCUPANT_TYPE = 'Minor';

// Stored types are mixed-case ('Home', 'home'); the select's option values are
// all lowercase.
const getPrimaryContactType = (
  entries: ?Array<Object>,
  typeKey: string,
  fallback: string,
): string =>
  (entries ?? [])
    .find(({ isPrimary }) => isPrimary)
    ?.[typeKey]?.toLowerCase() || fallback;

// The prospect is themselves the household's first adult, so an empty occupant
// list is a household of one rather than a missing answer — the same assertion
// the waitlist and application-creation flows already make.
const getHouseholdCounts = (
  additionalOccupants: ?Array<Object>,
): { adults: number, minors: number } => {
  const countOfType = (types) =>
    (additionalOccupants ?? []).filter(({ type }) => types.includes(type))
      .length;

  return {
    adults: 1 + countOfType(ADULT_OCCUPANT_TYPES),
    minors: countOfType([MINOR_OCCUPANT_TYPE]),
  };
};

// The prospect's own bedroom answer narrows their own floor plan, so the gate
// here is the one the rendered select will apply — seeding a plan that fails it
// would show a value the select has no option for.
const getPrefilledFloorPlan = ({
  preferredFloorPlanId,
  floorPlans,
  bedrooms,
}: {
  preferredFloorPlanId: ?string,
  floorPlans: ?Array<Object>,
  bedrooms: Array<string>,
}): string => {
  if (!preferredFloorPlanId) {
    return '';
  }

  const isOffered = filterFloorPlanOptions(
    mapFloorPlanOptions(floorPlans ?? []),
    bedrooms,
  ).some(({ value }) => value === preferredFloorPlanId);

  return isOffered ? preferredFloorPlanId : '';
};

// Only what the prospect actually answered is seeded; anything else opens blank.
// Nothing validates this form yet, so a fabricated value the agent does not
// notice would convert straight into applicant and waitlist data.
export const buildDefaultValues = ({
  currentProspect,
  floorPlans,
}: {
  currentProspect: ?Object,
  floorPlans: ?Array<Object>,
}): Object => {
  const preferences = currentProspect?.prospectPreferences;
  const { adults, minors } = getHouseholdCounts(
    currentProspect?.additionalOccupants,
  );
  const bedrooms = getPrefilledBedrooms({
    nBedsArr: preferences?.nBedsArr,
    nBeds: preferences?.nBeds,
    bedroomOptions: getBedroomOptions(floorPlans),
  });

  return {
    householdAnnualIncome: '',
    adults,
    minors,
    selectedPrograms: [],
    // A native Date, not a moment: the component library's date picker is
    // dayjs-backed and paints nothing for a moment instance.
    submissionDateTime: moment().toDate(),
    firstName: currentProspect?.firstName ?? '',
    middleName: currentProspect?.middleName ?? '',
    lastName: currentProspect?.lastName ?? '',
    suffix: currentProspect?.suffixId ?? '',
    phoneNumber: currentProspect?.phoneNumber ?? '',
    phoneType: getPrimaryContactType(
      currentProspect?.contact?.phones,
      'phoneType',
      DEFAULT_PHONE_ENTRY.phoneType,
    ),
    emailAddress: currentProspect?.emailAddress ?? '',
    emailType: getPrimaryContactType(
      currentProspect?.contact?.emails,
      'emailType',
      DEFAULT_EMAIL_ENTRY.emailType,
    ),
    dateOfBirth: null,
    gender: '',
    multiRace: [],
    ethnicity: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    bedrooms,
    floorPlan: getPrefilledFloorPlan({
      preferredFloorPlanId: preferences?.preferredFloorPlanId,
      floorPlans,
      bedrooms,
    }),
    requestAccommodation: '',
    accommodationRequested: '',
    // Exception to the blank-unless-answered rule above: the prospect form
    // makes this a required Yes/No that defaults to No, so false is the
    // prospect's answer rather than a missing one, and this field never opens
    // blank.
    accessibleUnit: preferences?.prefersUnitAccessible ? 'yes' : 'no',
    displacementStatusCodeId: '',
    preferences: [],
  };
};
